<template>
  <div class="root" :class="rootClass">
    <sp-label v-if="label" for="select" block>{{ label }}</sp-label>
    <sp-quantum-select
      id="select"
      ref="container"
      class="sp-select"
      navigation="flat"
      :hide-search-bar="hideSearchBarAttr"
      :max-results="props.maxResults"
      :disabled="disabledAttr"
      :fuzzy-search="fuzzySearchAttr"
      :close-on-select="closeOnSelectAttr"
      :class="modifierClass"
      :open="menuShown"
      :items="listItems"
      :item-title="itemTitle"
      :item-value="itemValue"
      :selected.prop="model"
      :multiple="multiple"
      :required="props.required"
      :appearance="menuAppearance"
      :hide-no-data="hideNoData"
      :aria-label="listLabel"
      :item-test-id="props.itemTestId"
      :highlight-first-result="props.highlightFirstResult"
      :highlight-idle-first-result="highlightIdleFirstResult"
      :wrap="props.wrap"
      :pointer="props.pointer"
      max-height="325px"
      selected-prepend-icon="check-alt-color"
      @update-open="handleMenuUpdateOpen"
      @search-enter="handleSearchEnter"
      @update-selected="handleUpdateSelected"
      @keydown.down="handleMenuKeyDown"
    >
      <!-- The forwarded slot shadows the panel's own fallback, so the text is repeated here. -->
      <slot slot="no-data" name="no-data">{{ noDataText }}</slot>
      <!-- On the wrapper, so a slotted activator gets the same close paths as the default field. -->
      <div
        slot="activator"
        class="sp-select-activator"
        @click="handleActivatorClick"
        @keydown.escape="closeMenu"
        @focusout="handleActivatorFocusOut"
      >
        <slot name="activator">
          <!-- `.attr` seeds the field; selection changes write the live `value` property below. -->
          <sp-text-field
            ref="activatorField"
            class="sp-select-activator__input"
            :inputmode="inputmode"
            :readonly="inputmode === 'none'"
            :value.attr="activatorText"
            :title="activatorTooltip"
            :aria-label="activatorLabel"
            :disabled="disabledAttr"
            :placeholder="activatorPlaceholder"
            :loading="loading"
            :density="activatorDensity"
            :variant="activatorVariant"
            propagate-append-click
            @click.prevent.stop="toggleMenuIntent"
            @keydown.enter="handleActivatorEnter"
            @keydown.tab="handleActivatorTab"
            @input="handleTextInput"
          >
            <div slot="prepend">
              <sp-icon
                v-if="selectedIcon"
                class="sp-select-activator__icon"
                :name="selectedIcon"
                aria-hidden="true"
              ></sp-icon>
              <slot name="prepend" @click="handlePrependClick"></slot>
            </div>
            <div slot="append" @click="handleAppendClick">
              <slot ref="appendSlot" name="append"></slot>
              <sp-caret v-if="showsCaret" :active="menuShown" :variant="props.caretVariant"></sp-caret>
            </div>
          </sp-text-field>
        </slot>
      </div>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { onClickOutside, useEventListener } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, toRef, useAttrs, useTemplateRef, watch, watchEffect } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { useHostElement } from "#ce/composables/useHostElement";
import { useItemIcons } from "./composables/useItemIcons";
import { t } from "#ce/shared/forms-i18n";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { hasSlotContent } from "#ce/utils/slots";
import { isObjectLike } from "#ce/utils/types";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as selectProps } from "./props.js";

// sp-select is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(formAssociatedHost());

const emit = defineEmits(["input", "text-input", "click-append", "click-prepend", "update-menu"]);
const { emitBubbling } = useHostEmit();
const hostElement = useHostElement();

const props = defineProps(selectProps);

const container = useTemplateRef("container");

const loading = computed(() => toBoolean(props.loading));
const returnObject = computed(() => toBoolean(props.returnObject));
const multiple = computed(() => toBoolean(props.multiple));
const searchable = computed(() => toBoolean(props.searchable));
// Bind null (not false) when searchable so the inner quantum-select never renders
// a stray `hide-search-bar="false"` — same pattern as `disabledAttr` below.
const hideSearchBarAttr = computed(() => !searchable.value || null);
// The idle list carries the hint only where there is no search bar to type into.
const highlightIdleFirstResult = computed(() => toBoolean(props.highlightFirstResult) && !searchable.value);
// Same null-not-false pattern for the fuzzy in-list matching opt-in.
const fuzzySearchAttr = computed(() => toBoolean(props.fuzzySearch) || null);
const keepOpenOnSelect = computed(() => toBoolean(props.keepOpenOnSelect));
// Same null-not-false attribute pattern: drop `close-on-select` from the inner
// quantum-select when the menu should stay open across selections.
const closeOnSelectAttr = computed(() => !keepOpenOnSelect.value || null);
// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });
// Bind null (not false) so the activator field never renders a stray `disabled="false"`,
// alongside the registration-level normalization.
const disabledAttr = computed(() => disabled.value || null);

const items = computed(() => {
  const items = [];

  if (props.prompt) {
    items.push({ [props.itemTitle]: props.prompt, [props.itemValue]: null, pendingChoice: false });
  }

  return [...items, ...props.items];
});

const menuOpen = ref(toBoolean(props.menu) && !disabled.value);
watch(menuOpen, (value) => emit("update-menu", value));

// A disabled select holds no open intent. While `menuBlocked` the picker reports no close for
// `handleMenuUpdateOpen` to act on, so the intent would otherwise outlive the disabled state and
// open on its own once items arrive.
watch(disabled, (value) => value && (menuOpen.value = false));

// `menuOpen` is the open intent and is what `update-menu` reports. While `menuBlocked` —
// `hide-no-data` with no items to list — the picker itself stays closed.
const menuBlocked = computed(() => toBoolean(props.hideNoData) && !(props.items?.length > 0));
const menuShown = computed(() => menuOpen.value && !menuBlocked.value);

// Only the falsy direction: `menu` permits the picker, it does not open it.
watch(
  () => toBoolean(props.menu),
  (allowed) => {
    if (!allowed) {
      menuOpen.value = false;
    }
  },
);

// A close reported while `menuBlocked` is the picker following `menuShown`, not a change of intent.
function handleMenuUpdateOpen({ detail }) {
  const [value] = detail;

  if (!value && menuBlocked.value) {
    return;
  }

  menuOpen.value = value;
}

function handleMenuKeyDown() {
  if (disabled.value) {
    return;
  }

  if (!menuOpen.value) {
    menuOpen.value = true;
  }
}

const modifierClass = computed(() => ({
  "--disabled": disabled.value,
  "--is-active": menuShown.value,
}));

const textAppearance = computed(() => props.appearance === "text");

// The text activator sizes itself to the selected label, so the dropdown cannot take its
// width from the activator — that is what the inline popup sizing already does.
const menuAppearance = computed(() => (textAppearance.value ? "inline" : props.appearance));

// The borderless compact activator is what makes the text appearance quiet, so it supplies
// both as defaults. An explicitly chosen variant or density still wins.
const activatorVariant = computed(() =>
  textAppearance.value && props.variant === "default" ? "plain" : props.variant,
);
const activatorDensity = computed(() =>
  textAppearance.value && props.density === "default" ? "compact" : props.density,
);

const rootClass = computed(() => ({
  "--wrap": toBoolean(props.wrap),
  "--is-active": menuShown.value,
  "--appearance-text": textAppearance.value,
}));

// The `value` attribute seeds the selection and names what a form reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value);

const model = ref(initialModel());

function initialModel(value = defaultValue.value) {
  return promptNormalized(toArray(value ?? []));
}

// An empty selection reads as the prompt row, wherever the write came from. Normalizing to the same
// array when there is nothing to do leaves the ref untouched, so this settles in one pass.
watch(model, () => {
  model.value = promptNormalized(model.value);
});

function promptNormalized(entries) {
  return entries.length === 0 && props.prompt ? [null] : entries;
}

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value;
    model.value = toArray(value ?? []);
  },
);

// The selection reads as the shape the `input` event carries: an array while `multiple`, the
// single entry otherwise.
const selection = computed(() => {
  const entries = returnObject.value ? model.value.map(resolvedItem) : model.value;
  return multiple.value ? entries : entries?.[0];
});

const { exposeProperties, exposeMethods } = useExpose();

exposeProperties({
  type: {
    configurable: true,
    get: () => (multiple.value ? "select-multiple" : "select-one"),
  },
  value: {
    configurable: true,
    get: () => selection.value,
    set: setSelection,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// An item carrying "" is reachable only through the menu — matching "" against the items would
// race async item loads.
function setSelection(value) {
  if (isNoSelection(value)) {
    model.value = [];
    return;
  }

  // A return-object consumer hands the item object back; the model stores its value. Without
  // `return-object` an object is the item's own value, so unwrapping it would lose the selection.
  model.value = toArray(value).map((entry) =>
    returnObject.value && isObjectLike(entry) ? getItemValue(entry) : entry,
  );
}

// A native <select multiple> submits one entry per selected option, so the multiple case hands
// setFormValue a FormData rather than a single string.
const { emitChange, mirrorFormValue, mirrorValueMissing, onFormReset, name: formName } = useForm();

const formValue = computed(() => {
  const values = model.value.filter((entry) => entry !== null && entry !== undefined);

  if (!multiple.value) {
    return String(values[0] ?? "");
  }

  if (!formName.value) {
    return null;
  }

  const data = new FormData();
  values.forEach((entry) => data.append(formName.value, String(entry)));

  return data;
});

mirrorFormValue(formValue);

// A required select is satisfied only once something other than the prompt is selected.
const isRequired = computed(() => isPresent(props.required));
const hasSelection = computed(() => model.value.some((entry) => entry !== null && entry !== undefined && entry !== ""));

mirrorValueMissing(
  () => isRequired.value && !hasSelection.value,
  () => t("select_value_missing"),
  container,
);

onFormReset(() => {
  model.value = initialModel();
  // A reset that lands on the value the model already holds moves no watcher, so the activator
  // keeps whatever the user typed unless it is written here.
  restoreActivatorText();
});

const textValue = computed(() => {
  if (multiple.value) {
    return model.value?.length ? `${model.value.length} selected` : "";
  }

  const item = findItem(model.value[0]);
  const title = getItemTitle(item);

  if (title) {
    const isPrompt = props.prompt && items.value[0] === item;
    const prepend = props.prependSelectedWith && !isPrompt ? props.prependSelectedWith : null;
    return [prepend, title].filter(Boolean).join(" ");
  }

  return undefined;
});

function findItem(value) {
  return items.value.find((item) => String(getItemValue(item)) === String(value));
}

// `resolvedItem` remembers the row it resolves, so the icon does not blink away while the list is
// replaced under an existing selection.
const selectedItem = computed(() => resolvedItem(model.value[0]));

const { listItems, selectedIcon, iconOnly } = useItemIcons(props, { items, selectedItem, multiple });

// A host attribute does not reach the activator on its own, so the aria-label is carried across.
const activatorText = computed(() => (iconOnly.value ? "" : textValue.value));
const activatorLabel = computed(() => {
  const hostLabel = hostElement?.getAttribute("aria-label");
  if (!iconOnly.value) {
    // The rendered `sp-label` points at the picker, not the input inside it, so the visible label
    // names the control here or nothing does.
    return hostLabel || props.label;
  }

  // `find(Boolean)` rather than `??`: an empty title or placeholder is not a name, and naming the
  // control "" is worse than falling through to the next candidate.
  return [getItemTitle(selectedItem.value), props.placeholder, hostLabel, props.label].find(Boolean);
});

// Names the popup listbox. Built from what names the control rather than from `activatorLabel`,
// whose icon-only branch falls back to the selected item's title — a list renamed on every pick.
// A visible `label` reaches the activator as an `aria-labelledby` relationship, which cannot cross
// into the list's shadow root, so its text is carried instead. `placeholder` is last and is what
// names an `sp-autocomplete`, which its documented usage supplies alone.
const listLabel = computed(() =>
  [hostElement?.getAttribute("aria-label"), props.label, props.placeholder].find(Boolean),
);

// Only an icon-only activator earns a tooltip: elsewhere the label it would carry is not the text
// on screen, so it would title the control with something other than what it reads.
const activatorTooltip = computed(() => (iconOnly.value ? activatorLabel.value : undefined));

// Icon-only shows no text at all, placeholder included — it survives as the activator's name.
const activatorPlaceholder = computed(() => (iconOnly.value ? undefined : props.placeholder));

// The row last resolved for each entry, so a `return-object` pick stays resolvable after the list
// is replaced under it, the way `resolvedTitles` keeps titles. The display keeps reading `findItem`
// itself, whose unresolved title is what tells the field to hold the user's text.
const resolvedItems = new Map();

function resolvedItem(value) {
  return findItem(value) ?? resolvedItems.get(String(value));
}

// Every entry the model holds is cached as soon as the list can resolve it, whichever channel wrote
// it, and only the current selection's rows are kept. Flushed synchronously so a write and a
// replacement of `items` in the same turn still cache against the list the write was made against.
watch([model, items], cacheResolvedItems, { immediate: true, flush: "sync" });

function cacheResolvedItems([entries]) {
  const keys = new Set(entries.map(String));

  resolvedItems.forEach((_, key) => {
    if (!keys.has(key)) {
      resolvedItems.delete(key);
    }
  });

  entries.forEach((entry) => {
    const found = findItem(entry);

    if (found !== undefined) {
      resolvedItems.set(String(entry), found);
    }
  });
}

// The title last resolved for each entry. Server-side filtering replaces `items` wholesale, so a
// selected entry can leave the list while it is still the selection and `findItem` stops resolving.
// Keyed the way `findItem` compares, so a value that changes type still matches.
const resolvedTitles = new Map();

watch(
  [() => model.value[0], textValue],
  ([entry, value]) => {
    if (value !== undefined) {
      resolvedTitles.set(String(entry), value);
    }
  },
  { immediate: true },
);

watchEffect(() => hostElement?.toggleAttribute("data-icon-only", iconOnly.value));

function titleForCurrentSelection() {
  return resolvedTitles.get(String(model.value[0]));
}

// The activator field pins user-typed text against attribute changes (the dirty value flag), so
// the selection's text is written to the live `value` property — the channel that always adopts.

const activatorField = useTemplateRef("activatorField");

watch([() => model.value[0], textValue], syncActivatorText);
watch(iconOnly, restoreActivatorText);

function syncActivatorText([entry, value], [previousEntry]) {
  if (!activatorField.value) {
    return;
  }

  // The `value` attribute seeds the field's default; its text is this live write, so icon-only is
  // enforced here rather than through the binding.
  if (iconOnly.value) {
    activatorField.value.value = "";
    return;
  }

  // An unchanged selection leaves the field alone while its title is unresolved: that is item-list
  // churn or the user typing over it, and the write lands once the title resolves.
  if (entry === previousEntry) {
    if (value === undefined && hasSelection.value) {
      return;
    }

    activatorField.value.value = value ?? "";
    return;
  }

  // A changed selection always writes, so the field cannot show a stale title. The remembered title
  // covers an entry the current `items` cannot resolve.
  activatorField.value.value = value ?? titleForCurrentSelection() ?? "";
}

// The activator shows the selection whenever the picker is closed, so text the user typed without
// picking anything cannot outlive the search it filtered. Typing writes the inner input directly,
// which no model watcher sees.
watch(menuOpen, (open) => {
  if (!open) {
    restoreActivatorText();
  }
});

// Whether Enter or Tab in the field commits the hinted row. The idle hint marks the first row of a
// list nobody has narrowed, and a readonly activator offers no way to narrow it, so a commit there
// would replace a selection the user never touched. `selectFirstResult()` commits either hint.
function fieldCommitsHint() {
  return toBoolean(props.highlightFirstResult) && (searchable.value || props.inputmode !== "none");
}

// WebKit confirms a composition with `isComposing` false and `keyCode` 229.
function isComposing(event) {
  return event.isComposing || event.keyCode === 229;
}

// Under `highlight-first-result`, Enter in the field commits the pending choice and the pick's own
// close applies (none under `keep-open-on-select`). Otherwise Enter closes the picker.
function handleActivatorEnter(event) {
  if (isComposing(event)) {
    return;
  }

  if (fieldCommitsHint() && selectFirstResult()) {
    event.preventDefault();

    if (!keepOpenOnSelect.value) {
      closeMenu();
    }

    return;
  }

  if (menuOpen.value) {
    menuOpen.value = false;
  }
}

// Tab commits the pending choice the same way and lets focus move on; Shift+Tab only moves.
function handleActivatorTab(event) {
  if (!isComposing(event) && !event.shiftKey && fieldCommitsHint()) {
    selectFirstResult();
  }
}

// Enter in the picker's own search bar, carrying its keyboard event.
function handleSearchEnter({ detail }) {
  const [event] = detail;

  if (toBoolean(props.highlightFirstResult) && !(event && isComposing(event))) {
    selectFirstResult();
  }
}

// While `menuBlocked` a click only opens the intent, so a click that moves the caret does not cancel
// a pending open.
function toggleMenuIntent() {
  if (disabled.value) {
    return;
  }

  menuOpen.value = menuBlocked.value ? true : !menuOpen.value;
}

// A slotted activator's click reaches the picker's own menu, which cancels its default action.
// While `menuBlocked` the click is taken here instead, cancelled the same way, so a native submit
// button does not submit its form.
function handleActivatorClick(event) {
  if (menuBlocked.value) {
    event.preventDefault();
    event.stopPropagation();
    toggleMenuIntent();
  }
}

// A press on a row retargets `relatedTarget` to the container, so it reads as focus staying inside.
// A shown bottom sheet (the picker's `inSheetMode`) makes the activator inert, so its focus leaves
// with no `relatedTarget`, and no other control can take focus until the sheet is dismissed, which
// is the close on that path. A blocked picker shows no sheet, so there the leave drops the open
// intent.
function handleActivatorFocusOut(event) {
  if (container.value?.contains(event.relatedTarget)) {
    return;
  }

  if (menuOpen.value) {
    if (!(container.value?.inSheetMode && menuShown.value)) {
      menuOpen.value = false;
    }
    return;
  }

  restoreActivatorText();
}

function restoreActivatorText() {
  if (!activatorField.value) {
    return;
  }

  if (iconOnly.value) {
    activatorField.value.value = "";
    return;
  }

  const title = textValue.value ?? titleForCurrentSelection();

  // An unresolved title on a live selection is item-list churn, not an empty field.
  if (title === undefined && hasSelection.value) {
    return;
  }

  activatorField.value.value = title ?? "";
}

const hasItems = computed(() => props.items?.length > 0);
const caretVisible = computed(() => hasItems.value);

// Slotted append content replaces the caret. Read through the flattened assignment, so a forwarding
// slot with nothing behind it counts as empty.
const appendSlot = useTemplateRef("appendSlot");
const hasAppendContent = ref(false);

function readAppendContent() {
  hasAppendContent.value = hasSlotContent(appendSlot.value);
}

useEventListener(appendSlot, "slotchange", readAppendContent);
onMounted(readAppendContent);

const showsCaret = computed(() => caretVisible.value && !hasAppendContent.value);

function getItemTitle(item) {
  return getItemAttribute(item, props.itemTitle);
}

function getItemValue(item) {
  return getItemAttribute(item, props.itemValue);
}

function getItemAttribute(item, attribute) {
  return isObjectLike(item) ? item[attribute] : item;
}

function handlePrependClick() {
  emit("click-prepend");
}

function handleAppendClick() {
  emit("click-append");
}

function handleTextInput({ detail }) {
  const [value] = detail;
  emit("text-input", value);
}

// Commits the shown picker's highlighted row through its user path, so it reports like a click.
// Returns the caller's own item, or null when the picker is hidden or disabled, no row is
// highlighted, or the highlighted row is already the selection.
function selectFirstResult() {
  if (disabled.value || !menuShown.value) {
    return null;
  }

  const committed = container.value?.selectFirstResult?.({ markedOnly: true });

  // The picker reports its rendered row, a decorated copy carrying the hint.
  return committed ? (findItem(getItemValue(committed)) ?? committed) : null;
}

exposeMethods({ selectFirstResult });

function handleUpdateSelected({ detail }) {
  const [value] = detail;
  model.value = promptNormalized(value);
  // Emitted here, where the user's pick enters, rather than from a watcher on the model: a watcher
  // also fires for attribute syncs, property writes and form resets — which is both wrong (`input`
  // reports user input, as a native control does) and a flush late, long enough for a host's value
  // binding to be re-applied with the value the pick has not reached yet.
  emitBubbling("input", selection.value);
  // A native select reports a pick with `input` then `change`, both at the pick rather than on
  // blur, and `multiple` dispatches on every pick that lands.
  emitChange();

  if (keepOpenOnSelect.value) {
    return;
  }

  // This is required to close the menu after a selection is made and all events have been handled.
  nextTick(closeMenu);
}

onClickOutside(container, closeMenu);

function closeMenu() {
  menuOpen.value = false;
}
</script>

<style>
:host {
  display: block;
  height: fit-content;
  --sp-comp-divider-color: var(--sp-comp-select-divider-color, #ccc);
  --sp-comp-quantum-select-popup-min-height: 0;
  --sp-comp-popup-min-height: 0;
  /* Shield from ancestor --sp-comp-popup-width so the popup matches the activator. */
  --sp-comp-popup-width: 100%;
}

/* An icon is the whole control here, so it takes an icon's width rather than its container's. The
   popup still opens at its own width, which --appearance-text sets to max-content. Keyed off the
   resolved mode, since `icon-only` with no icon to show still renders its title. */
:host([data-icon-only]) {
  display: inline-block;
  --sp-comp-popup-width: max-content;
}
</style>

<style lang="scss" scoped>
.root {
  --sp-comp-popup-min-width: 100% !important;
  --sp-comp-list-item-content-white-space: nowrap;
  --caret-size: var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, var(--sp-sys-icon-size-medium)));

  &.--wrap {
    --sp-comp-quantum-select-popup-width: 0;
    --sp-comp-list-item-content-white-space: normal;
  }

  &.--is-active {
    --sp-comp-text-field-border-color: var(
      --sp-comp-select-active-border-color,
      var(--sp-sys-form-field-focus-border-color, var(--sp-sys-color-primary))
    );
  }

  &.--appearance-text {
    /* `em` throughout, so the control takes the surrounding text's size. */
    --sp-comp-text-field-font-size: 1em;
    --sp-comp-text-field-compact-min-height: var(--sp-comp-select-text-min-height, 1.5rem);
    --caret-size: var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, 1.25em));
    /* The caret is box-centred here, so both of its optical nudges for inline use come off. */
    --sp-comp-caret-offset: 0;
    --sp-comp-caret-active-nudge: 0;

    /* The field hugs the selected label instead of filling its container. */
    --sp-comp-text-field-min-width: 0;
    --sp-comp-text-field-width: auto;
    --sp-comp-text-field-field-sizing: content;

    /* The dropdown grows to its widest entry, which the hugging activator can be narrower than. */
    --sp-comp-popup-width: max-content;

    /* Only the activator takes the surrounding text's size. The rows state a size of their own,
       because `sp-list-item` sizes its title by inheritance wherever the typescale tokens are
       undefined — as they are in admin — which would otherwise shrink the menu with the activator. */
    --sp-comp-list-item-title-font-size: var(--sp-sys-typescale-body-font-size, 1rem);
  }
}

.sp-select-activator__input[inputmode="none"] {
  --sp-comp-text-field-cursor: pointer;
}

.sp-select-list-container {
  position: relative;
  width: 100%;
}

sp-label {
  margin-bottom: var(--sp-comp-select-label-margin-bottom, var(--sp-ref-scale-6));
  --sp-comp-text-label-short: var(--sp-comp-select-label-font, var(--sp-sys-form-field-label-short));
}

sp-caret {
  --sp-comp-caret-size: var(--caret-size);
  color: var(--sp-comp-select-caret-color, currentColor);
}

[slot="append"],
[slot="prepend"] {
  display: flex;
  align-items: center;
  justify-content: center;
}

[slot="append"] {
  padding-inline-end: var(--sp-comp-select-append-padding-inline-end, 0);
}

.sp-select-list__no-result {
  padding: var(--sp-comp-select-no-result-padding, 1rem);
  color: var(--sp-comp-select-no-result-color, #aaa);
}
</style>
